<script>
    import radio from "../../stores/radio.js";
    import journal from "../../stores/journal.js";
    import talkie from "../../stores/talkie.js";
    import localTime from "../../stores/localTime.js";
    import Button from "../windows/Button.svelte";
    import Display from "../windows/Display.svelte";
    import t, { currentLocale } from "../../localization/t.js";

    const TUNING_TIMEOUT_MS = 5000;

    let staticPlayer;
    let streamPlayer;

    // state
    let isPlaying = false;
    let isTuning = false;
    let isMuted = false;
    let playlist = [];
    let indexForCurrentStation = 0;
    let displayText;
    let tuningTimeout;

    radio.subscribe((stations) => {
        if (!stations) {
            return;
        }

        const hasChanges = stations.some((station) => playlist.find(({ url }) => station.url === url) === undefined);

        if (hasChanges) {
            playlist = stations;
            indexForCurrentStation = 0;

            const shouldPlayTuningNoise = isPlaying === true && isTuning === false;

            if (shouldPlayTuningNoise === true) {
                isTuning = true;
            }
        }
    });

    // display time & date when radio is off
    localTime.subscribe((time) => {
        if (!time) {
            return;
        }

        const { date } = time;

        if (isPlaying) {
            return;
        }

        const timestamp =
            date.toLocaleTimeString(currentLocale(), {
                hour: "numeric",
                minute: "numeric"
            }) +
            "\n" +
            date.toLocaleDateString(currentLocale(), {
                weekday: "short",
                month: "short",
                day: "numeric"
            });

        displayText = timestamp;
    });

    // control static player
    $: (() => {
        if (!staticPlayer) {
            return;
        }

        if (isTuning) {
            const current = playlist[indexForCurrentStation];

            if (current) {
                journal.remember(`listened to "${current.name}" radio station`);
            } else {
                journal.remember("couldn't tune into any local radio station");
            }

            setTimeout(() => {
                displayText = current ? `${current.name}\n${current.town}, ${current.state}` : t("radio.no_reception");
            }, 40);

            clearTimeout(tuningTimeout);

            tuningTimeout = setTimeout(() => {
                if (!isTuning) {
                    return;
                }

                didFailToStartPlaybak();
                didClickNextStationButton();
            }, 5000);

            staticPlayer.play().catch(didFailToStartPlaybak);
        } else {
            staticPlayer.pause();
        }
    })();

    // control stream player
    $: (() => {
        if (!streamPlayer) {
            return;
        }

        const canPlay = playlist[indexForCurrentStation];

        if (isPlaying && canPlay) {
            streamPlayer.src = playlist[indexForCurrentStation].url;
            streamPlayer.play().catch(didFailToStartPlaybak);
        } else {
            streamPlayer.pause();
            streamPlayer.src = "";
        }
    })();

    // mute radio when talking
    let shouldUnmute = false;

    $: (() => {
        if ($talkie.isTalking) {
            if (!isMuted) {
                shouldUnmute = true;
                didClickMuteButton();
            }
        } else if (shouldUnmute) {
            shouldUnmute = false;
            didClickMuteButton();
        }
    })();

    // events
    function didClickPowerButton() {
        isPlaying = !isPlaying;
        isTuning = isPlaying;
    }

    function didFailToStartPlaybak() {
        isTuning = false;

        displayText = displayText
            .split(/\n/)
            .map((line, index) => (index === 0 ? line : t("radio.low_signal")))
            .join("\n")
            .toUpperCase();
    }

    function didClickNextStationButton() {
        if (indexForCurrentStation === playlist.length - 1) {
            indexForCurrentStation = 0;
        } else {
            indexForCurrentStation += 1;
        }

        displayText = displayText
            .split(/\n/)
            .map((line) => {
                const characters = line.split("");
                return characters.map(() => characters[Math.trunc(Math.random() * characters.length)]).join("");
            })
            .join("\n");

        isTuning = true;
        streamPlayer.src = playlist[indexForCurrentStation].url;
    }

    function didClickMuteButton() {
        isMuted = !isMuted;
    }

    function didStartPlayback() {
        isTuning = false;
    }
</script>

<div class="radio">
    <Display text={displayText} rate={50} />
    <div class="spacer" />
    <div class="controls">
        <Button action={didClickPowerButton}>
            {isPlaying ? t("radio.turn_off") : t("radio.turn_on")}
        </Button>
        <div class="spacer" />
        <Button action={didClickNextStationButton} isEnabled={isPlaying && !isTuning}>
            {t("radio.next_station")}
        </Button>
        <div class="flexible spacer" />
        <Button action={didClickMuteButton}>
            {isMuted ? t("radio.unmute") : t("radio.mute")}
        </Button>
    </div>

    <audio src="/static.m4a" loop preload bind:this={staticPlayer} bind:muted={isMuted} />

    <audio preload bind:this={streamPlayer} bind:muted={isMuted} on:playing={didStartPlayback} />
</div>

<style>
    .radio {
        display: flex;
        flex-direction: column;
        padding: var(--spacing);
    }

    .controls {
        display: flex;
    }

    .spacer {
        width: var(--spacing);
        height: var(--spacing);
    }

    .spacer.flexible {
        flex-grow: 1;
    }
</style>
